import { useState, useEffect, useRef } from 'react';
import { getBookings, getWorkers } from '../services/api';

export default function MapView() {
  const [bookings, setBookings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, in_progress
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    loadData();
    loadLeaflet();
  }, []);

  useEffect(() => {
    // Initialize map once loading is done and Leaflet is loaded
    if (!loading && window.L && !mapInstance.current && mapRef.current) {
      initMap();
    }
    if (mapInstance.current && !loading) {
      renderMarkers();
    }
  }, [bookings, workers, filter, loading]);

  const loadLeaflet = () => {
    if (window.L) return; // already loaded
    if (document.getElementById('leaflet-css')) return;
    const css = document.createElement('link');
    css.id = 'leaflet-css';
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      // Map will be initialized by the useEffect once loading is false
    };
    document.head.appendChild(script);
  };

  const initMap = () => {
    if (mapInstance.current || !mapRef.current || !window.L) return;
    const L = window.L;
    mapInstance.current = L.map(mapRef.current).setView([18.4386, 79.1288], 10);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(mapInstance.current);
    renderMarkers();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [b, w] = await Promise.all([getBookings(), getWorkers()]);
      setBookings(b);
      setWorkers(w);
    } catch (e) {}
    setLoading(false);
  };

  const renderMarkers = () => {
    const L = window.L;
    if (!L || !mapInstance.current) return;

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Filter bookings
    const activeBookings = filter === 'all'
      ? bookings.filter(b => ['pending', 'confirmed', 'in_progress'].includes(b.status))
      : bookings.filter(b => b.status === filter);

    // Booking markers (red/orange)
    activeBookings.forEach(b => {
      if (!b.latitude || !b.longitude) return;
      const statusColors = { pending: '#FF9800', confirmed: '#6C63FF', in_progress: '#2196F3' };
      const color = statusColors[b.status] || '#FF9800';
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:${color};color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">#${b.id}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      });
      const marker = L.marker([b.latitude, b.longitude], { icon })
        .addTo(mapInstance.current)
        .bindPopup(`
          <div style="min-width:200px">
            <strong>Booking #${b.id}</strong><br/>
            <b>Service:</b> ${b.service?.name || '—'}<br/>
            <b>Customer:</b> ${b.customer?.name || '—'} (${b.customer?.phone || '—'})<br/>
            <b>Status:</b> <span style="color:${color};font-weight:bold">${b.status}</span><br/>
            <b>Worker:</b> ${b.worker?.user?.name || 'Unassigned'}<br/>
            <b>Amount:</b> ₹${b.total_amount}<br/>
            <b>Address:</b> ${b.address || '—'}
          </div>
        `);
      markersRef.current.push(marker);
    });

    // Worker markers (green)
    workers.forEach(w => {
      if (!w.latitude || !w.longitude) return;
      const available = w.is_available;
      const color = available ? '#4CAF50' : '#999';
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:${color};color:#fff;border-radius:8px;padding:4px 8px;font-size:11px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);white-space:nowrap;">🔧 ${w.user?.name?.split(' ')[0] || 'W'}</div>`,
        iconSize: [80, 28],
        iconAnchor: [40, 14]
      });
      const marker = L.marker([w.latitude, w.longitude], { icon })
        .addTo(mapInstance.current)
        .bindPopup(`
          <div style="min-width:180px">
            <strong>🔧 ${w.user?.name || 'Worker'}</strong><br/>
            <b>Phone:</b> ${w.user?.phone || '—'}<br/>
            <b>Skill:</b> ${w.skill_category}<br/>
            <b>Rating:</b> ${w.rating} ⭐<br/>
            <b>Available:</b> ${available ? '✅ Yes' : '❌ No'}<br/>
            <b>ID:</b> ${w.id}
          </div>
        `);
      markersRef.current.push(marker);
    });

    // Auto-fit bounds
    const allCoords = [
      ...activeBookings.filter(b => b.latitude && b.longitude).map(b => [b.latitude, b.longitude]),
      ...workers.filter(w => w.latitude && w.longitude).map(w => [w.latitude, w.longitude])
    ];
    if (allCoords.length > 0) {
      mapInstance.current.fitBounds(allCoords, { padding: [50, 50] });
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>🗺️ Live Map View</h1>
        <p>View bookings and workers on map for easy assignment</p>
      </div>
      <div className="card">
        <div className="card-header">
          <h2>Bookings & Workers Map</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
            >
              <option value="all">All Active</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
            </select>
            <button className="refresh-btn" onClick={loadData}>🔄 Refresh</button>
            <span style={{ fontSize: 12, color: '#666' }}>
              🟠 Pending &nbsp; 🟣 Confirmed &nbsp; 🔵 In Progress &nbsp; 🟢 Worker Available &nbsp; ⚫ Worker Busy
            </span>
          </div>
        </div>
        {loading ? (
          <div className="loading">Loading map data...</div>
        ) : (
          <div ref={mapRef} style={{ height: 600, borderRadius: 12, marginTop: 12 }}></div>
        )}
      </div>
    </div>
  );
}
